// Central medication catalog — single source of truth
// Used by: homepage Medication Wall, Women's Shop, Men's Garage,
// and individual medication detail pages.

export const ACCENT = {
  pink: {
    text: "text-[var(--leen-pink)]",
    bg: "bg-[var(--leen-pink)]",
    border: "border-[var(--leen-pink)]",
    softBg: "bg-[var(--leen-pink)]/15",
    softBorder: "border-[var(--leen-pink)]/40",
    btn: "btn-pink",
  },
  blue: {
    text: "text-[var(--leen-blue)]",
    bg: "bg-[var(--leen-blue)]",
    border: "border-[var(--leen-blue)]",
    softBg: "bg-[var(--leen-blue)]/15",
    softBorder: "border-[var(--leen-blue)]/40",
    btn: "btn-blue",
  },
  yellow: {
    text: "text-[var(--leen-yellow)]",
    bg: "bg-[var(--leen-yellow)]",
    border: "border-[var(--leen-yellow)]",
    softBg: "bg-[var(--leen-yellow)]/15",
    softBorder: "border-[var(--leen-yellow)]/40",
    btn: "btn-pink",
  },
  amber: {
    text: "text-[#D67D3E]",
    bg: "bg-[#D67D3E]",
    border: "border-[#D67D3E]",
    softBg: "bg-[#D67D3E]/15",
    softBorder: "border-[#D67D3E]/40",
    btn: "btn-pink",
  },
};

export const MEDICATIONS = {
  semaglutide: {
    slug: "semaglutide",
    name: "SEMAGLUTIDE",
    category: "Weight Loss",
    type: "Weekly Injection",
    accent: "pink",
    capColor: "#EC2C7E",
    bodyColor: "#F4A8C8",
    bottleType: "vial",
    label: "SEMA",
    tagline: "The GLP-1 that changed everything.",
    bullets: ["Appetite Control", "Sustainable Results", "Weekly Injection"],
    forSide: "both",
    what:
      "Semaglutide is a once-weekly GLP-1 receptor agonist that helps regulate appetite, slow gastric emptying, and reset your relationship with food. Originally developed for type 2 diabetes, it's now the most-prescribed clinical weight-loss medication in the world.",
    benefits: [
      { title: "Lose 15–20%+ of body weight", body: "Clinical studies show sustained, double-digit weight loss over 12 months." },
      { title: "Curb cravings & food noise", body: "Quieter appetite signals so you finally stop thinking about food all day." },
      { title: "Improve metabolic health", body: "Better blood sugar, lower A1C, healthier cardiovascular markers." },
      { title: "Built for long-term use", body: "Decade-plus safety profile when prescribed and titrated correctly." },
    ],
    how: [
      { n: "01", title: "Mimics GLP-1", body: "Activates the GLP-1 receptor that controls hunger and insulin." },
      { n: "02", title: "Slows digestion", body: "You feel fuller longer on smaller portions." },
      { n: "03", title: "Resets appetite", body: "Food cravings and snacking drop, naturally." },
      { n: "04", title: "Weekly dose", body: "One small subcutaneous injection per week." },
    ],
    stats: [
      { v: "15%", k: "average body weight lost" },
      { v: "12mo", k: "ongoing clinical care" },
      { v: "24h", k: "clinician review" },
    ],
    faq: [
      { q: "Is Semaglutide safe?", a: "Semaglutide has a decade of clinical data and is FDA-approved for chronic weight management when prescribed by a licensed clinician." },
      { q: "How fast will I see results?", a: "Most patients see noticeable weight loss within 4–8 weeks. Best results emerge over 6–12 months." },
      { q: "What are the side effects?", a: "Most common: mild nausea, especially during dose titration. Side effects fade as your body adjusts." },
      { q: "Do I have to inject it myself?", a: "Yes — but it's a small, painless once-weekly injection in the thigh or abdomen. We walk you through it." },
    ],
  },
  tirzepatide: {
    slug: "tirzepatide",
    name: "TIRZEPATIDE",
    category: "Weight Loss",
    type: "Weekly Injection",
    accent: "blue",
    capColor: "#1E7AE6",
    bodyColor: "#8FB6E8",
    bottleType: "vial",
    label: "TIRZ",
    tagline: "Dual-action. Next-generation weight loss.",
    bullets: ["More Weight Loss", "Better Blood Sugar", "Dual Action Power"],
    forSide: "both",
    what:
      "Tirzepatide is a dual GIP/GLP-1 receptor agonist — the next generation of weight-loss medicine. It outperforms semaglutide head-to-head in clinical trials and is rapidly becoming the gold standard.",
    benefits: [
      { title: "Lose 20–25%+ of body weight", body: "Clinical data shows tirzepatide outperforms semaglutide on weight loss." },
      { title: "Dual receptor action", body: "Targets both GIP and GLP-1 pathways for stronger metabolic effect." },
      { title: "Powerful glucose control", body: "Significant improvements in insulin sensitivity and A1C." },
      { title: "Once weekly", body: "Same simple weekly injection cadence as semaglutide." },
    ],
    how: [
      { n: "01", title: "Activates GIP + GLP-1", body: "Hits two metabolic pathways at once for compounding effect." },
      { n: "02", title: "Slows gastric emptying", body: "Long-lasting fullness from smaller meals." },
      { n: "03", title: "Improves fat metabolism", body: "Better insulin response means more fat-burning, less storage." },
      { n: "04", title: "Weekly titration", body: "Carefully stepped-up dosing minimizes side effects." },
    ],
    stats: [
      { v: "22%", k: "average body weight lost" },
      { v: "2x", k: "vs. older GLP-1s" },
      { v: "12mo", k: "of clinical care" },
    ],
    faq: [
      { q: "Is tirzepatide better than semaglutide?", a: "Head-to-head studies show greater average weight loss on tirzepatide. Your clinician will recommend what fits your goals and history." },
      { q: "Is it covered by insurance?", a: "We work directly with compounding pharmacies — no insurance required. Flat monthly pricing." },
      { q: "Can I switch from semaglutide?", a: "Yes — many patients do. Your clinician will design a safe titration plan." },
      { q: "What if I plateau?", a: "Plateaus are normal. Your care team will adjust dose, nutrition guidance, and protocol." },
    ],
  },
  "oral-glp1": {
    slug: "oral-glp1",
    name: "ORAL GLP-1",
    category: "Weight Loss",
    type: "Daily Tablets",
    accent: "pink",
    capColor: "#FFFFFF",
    bodyColor: "#FFFFFF",
    bottleType: "bottle",
    label: "ORAL\nGLP-1",
    tagline: "GLP-1 power. No needles.",
    bullets: ["Easy To Take", "Daily Support", "Appetite Control"],
    forSide: "both",
    what:
      "Oral GLP-1 delivers the same appetite-regulating mechanism as the leading injectables — without the needle. A daily tablet that fits into any routine.",
    benefits: [
      { title: "No injections", body: "Take it with a glass of water, first thing in the morning." },
      { title: "Daily appetite control", body: "Smoother, steadier effect than weekly injections for some patients." },
      { title: "Ideal for needle-shy patients", body: "Same mechanism, friendlier delivery." },
      { title: "Easy to titrate", body: "Adjust quickly with your clinician — no waiting a week between changes." },
    ],
    how: [
      { n: "01", title: "Daily tablet", body: "Taken once daily on an empty stomach." },
      { n: "02", title: "GLP-1 activation", body: "Engages the same appetite-regulating receptor." },
      { n: "03", title: "Steady levels", body: "Daily dosing keeps blood levels even." },
      { n: "04", title: "Adjustable", body: "Your clinician can tune dose weekly as needed." },
    ],
    stats: [
      { v: "0", k: "needles required" },
      { v: "Daily", k: "easy routine" },
      { v: "12mo", k: "ongoing care" },
    ],
    faq: [
      { q: "How is this different from injectable semaglutide?", a: "Same molecule family, different delivery. Less peak-and-trough, more steady-state." },
      { q: "Is it as effective as injections?", a: "Effective for most patients — your clinician will help you choose what's right for your goals." },
      { q: "Any food restrictions?", a: "Take it on an empty stomach with water; wait 30 minutes before food." },
      { q: "Can I switch later?", a: "Yes. Many patients start oral and move to injectable, or vice-versa." },
    ],
  },
  testosterone: {
    slug: "testosterone",
    name: "TESTOSTERONE",
    category: "Performance",
    type: "Weekly Injection",
    accent: "blue",
    capColor: "#1E7AE6",
    bodyColor: "#9EBFE0",
    bottleType: "vial",
    label: "TEST",
    tagline: "Restore the engine.",
    bullets: ["Build Muscle", "Boost Drive", "Optimize Health"],
    forSide: "men",
    what:
      "Testosterone Replacement Therapy (TRT) restores natural testosterone levels in men with clinically low T. Done right — bloodwork-driven, clinician-titrated — it's one of the most life-changing protocols in men's medicine.",
    benefits: [
      { title: "More energy & drive", body: "End the 3 p.m. crash. Wake up wanting to move." },
      { title: "Lean muscle & strength", body: "Faster recovery and noticeably better gym output." },
      { title: "Sharper focus & mood", body: "Cognitive clarity, less irritability, better sleep." },
      { title: "Better body composition", body: "Less visceral fat, more lean mass over time." },
    ],
    how: [
      { n: "01", title: "Bloodwork first", body: "Total T, free T, estradiol, hematocrit, lipid panel." },
      { n: "02", title: "Clinician review", body: "Diagnosis only when clinically appropriate — not on demand." },
      { n: "03", title: "Personalized protocol", body: "Dose, frequency, and ancillaries dialed to YOUR labs." },
      { n: "04", title: "Quarterly check-ins", body: "Ongoing labs and adjustments. Care doesn't stop after shipment." },
    ],
    stats: [
      { v: "+18", k: "lb avg. lean mass (12 mo)" },
      { v: "+38%", k: "energy reported" },
      { v: "4x/yr", k: "labs included" },
    ],
    faq: [
      { q: "Will I have to be on it forever?", a: "Often yes. TRT is a replacement therapy — your body stops producing as much endogenous testosterone." },
      { q: "Are there risks?", a: "When monitored properly with quarterly labs: minimal. Without monitoring: significant. We monitor." },
      { q: "Can I have kids on TRT?", a: "Fertility considerations matter. Talk to your clinician about adjunct protocols like HCG or enclomiphene." },
      { q: "Injection or cream?", a: "We primarily prescribe weekly intramuscular or subcutaneous injection — most predictable levels." },
    ],
  },
  enclomiphene: {
    slug: "enclomiphene",
    name: "ENCLOMIPHENE",
    category: "Performance",
    type: "Daily Tablets",
    accent: "blue",
    capColor: "#1E7AE6",
    bodyColor: "#B7D2F1",
    bottleType: "bottle",
    label: "ENCLO",
    tagline: "Boost your own T. Preserve fertility.",
    bullets: ["Boost Natural T", "Preserve Fertility", "No Injections"],
    forSide: "men",
    what:
      "Enclomiphene is an oral selective estrogen receptor modulator (SERM) that stimulates your body's own testosterone production — without shutting down fertility like exogenous TRT can.",
    benefits: [
      { title: "Boost your own T", body: "Signals your pituitary to produce more LH and FSH." },
      { title: "Preserve fertility", body: "Unlike TRT, doesn't suppress sperm production." },
      { title: "Daily tablet", body: "No injections, no scheduling around shots." },
      { title: "Reversible", body: "Stop anytime and your baseline returns." },
    ],
    how: [
      { n: "01", title: "Daily pill", body: "One small tablet, taken once a day." },
      { n: "02", title: "Pituitary signal", body: "Tells your brain to drive natural T production." },
      { n: "03", title: "Endogenous T rises", body: "Your own testosterone levels climb over 4–12 weeks." },
      { n: "04", title: "Lab-monitored", body: "Quarterly bloodwork to dial the dose." },
    ],
    stats: [
      { v: "+150%", k: "avg total T increase" },
      { v: "Daily", k: "oral dose" },
      { v: "0", k: "fertility impact" },
    ],
    faq: [
      { q: "Is this the same as Clomid?", a: "Enclomiphene is the active isomer of clomiphene — cleaner side-effect profile and more targeted." },
      { q: "Who is it for?", a: "Men with low or borderline T who want to preserve fertility or avoid injections." },
      { q: "Can I stack with TRT?", a: "Not typically — they work in opposite directions. Your clinician will choose one path." },
      { q: "Side effects?", a: "Mostly mild. Some patients report mood changes or visual disturbances — monitored at every check-in." },
    ],
  },
  "mic-b12": {
    slug: "mic-b12",
    name: "MIC + B12",
    category: "Wellness",
    type: "Weekly Injection",
    accent: "amber",
    capColor: "#C8632A",
    bodyColor: "#C29066",
    bottleType: "vial",
    label: "MIC\nB12",
    tagline: "Energy, metabolism, fat burn.",
    bullets: ["Boost Energy", "Burn Fat", "Improve Metabolism"],
    forSide: "both",
    what:
      "A lipotropic injection blend — Methionine, Inositol, Choline, plus B12 — that supports fat metabolism, energy, and liver function. Used as a complement to weight-loss protocols and standalone wellness.",
    benefits: [
      { title: "All-day energy", body: "B12 boost without the caffeine spike." },
      { title: "Supports fat metabolism", body: "Lipotropic compounds help mobilize stored fat." },
      { title: "Liver support", body: "Choline + methionine support healthy liver function." },
      { title: "Stacks with GLP-1", body: "Common adjunct for GLP-1 patients wanting more energy." },
    ],
    how: [
      { n: "01", title: "Weekly injection", body: "Small intramuscular shot, often self-administered." },
      { n: "02", title: "Lipotropic action", body: "MIC compounds support fat transport and breakdown." },
      { n: "03", title: "B12 energy", body: "Bypasses gut absorption issues for full uptake." },
      { n: "04", title: "Ongoing protocol", body: "Most patients run it monthly to quarterly." },
    ],
    stats: [
      { v: "Weekly", k: "easy injection" },
      { v: "B12+MIC", k: "lipotropic blend" },
      { v: "0", k: "stimulants" },
    ],
    faq: [
      { q: "Will this help me lose weight on its own?", a: "It supports the process — best results stacked with a primary protocol like GLP-1 or TRT." },
      { q: "Any side effects?", a: "Generally well tolerated. Some patients feel a mild energy lift the first day." },
      { q: "Can I use it long-term?", a: "Yes — many patients run it on a maintenance schedule." },
      { q: "Do I need bloodwork?", a: "Not always for MIC+B12 alone, but recommended if you're combining with other protocols." },
    ],
  },
  "l-carnitine": {
    slug: "l-carnitine",
    name: "L-CARNITINE",
    category: "Wellness",
    type: "Weekly Injection",
    accent: "yellow",
    capColor: "#F2C94C",
    bodyColor: "#E8D87A",
    bottleType: "vial",
    label: "L-CAR",
    tagline: "Turn fat into fuel.",
    bullets: ["Burn More Fat", "Increase Energy", "Workout Support"],
    forSide: "both",
    what:
      "L-Carnitine shuttles fatty acids into mitochondria so your body can actually burn the fat it's storing. A clinical-grade complement to weight loss and athletic performance.",
    benefits: [
      { title: "Better fat oxidation", body: "Moves fatty acids into the mitochondria — where they get burned." },
      { title: "Endurance & recovery", body: "Athletes report better workouts and less soreness." },
      { title: "Cognitive clarity", body: "Linked to improved focus and reduced mental fatigue." },
      { title: "Stacks with everything", body: "Pairs well with GLP-1, TRT, and MIC+B12." },
    ],
    how: [
      { n: "01", title: "Weekly injection", body: "Small subcutaneous dose, once a week." },
      { n: "02", title: "Mitochondrial transport", body: "Carnitine delivers fatty acids to your cellular furnaces." },
      { n: "03", title: "Energy from fat", body: "Your body becomes more efficient at fueling from stored fat." },
      { n: "04", title: "Track progress", body: "Bodyweight, body composition, and energy reviewed quarterly." },
    ],
    stats: [
      { v: "Weekly", k: "injection" },
      { v: "Fat→Fuel", k: "mitochondrial transport" },
      { v: "12mo", k: "care included" },
    ],
    faq: [
      { q: "Will it make me jittery?", a: "No — it's not a stimulant. Energy comes from better fat utilization." },
      { q: "Can I take orally?", a: "Oral L-carnitine has poor absorption. Injection is significantly more bioavailable." },
      { q: "Athletes use this?", a: "Yes — endurance and recovery benefits are well documented." },
      { q: "Pair with GLP-1?", a: "Common stack. Your clinician will design the protocol." },
    ],
  },
  tadalafil: {
    slug: "tadalafil", name: "TADALAFIL", category: "Performance", type: "Daily Tablet",
    accent: "blue", capColor: "#1E7AE6", bodyColor: "#9EBFE0", bottleType: "bottle", label: "TADA",
    tagline: "Confidence on demand. Daily or as needed.",
    bullets: ["Erectile Function", "Daily Or As-Needed", "Up To 36 Hours"],
    forSide: "men",
    what: "Tadalafil is a PDE-5 inhibitor — the same class as Cialis. Taken daily at a low dose, it improves blood flow and erectile function with a longer-lasting effect than alternatives.",
    benefits: [
      { title: "Up to 36 hours of effect", body: "Long-acting half-life — spontaneity, not scheduling." },
      { title: "Daily low-dose option", body: "Smoother, more consistent performance vs. on-demand." },
      { title: "Improves vascular health", body: "Modest blood-pressure benefits documented." },
      { title: "Discreet & affordable", body: "Generic compounded options at flat monthly pricing." },
    ],
    how: [
      { n: "01", title: "Daily tablet", body: "Small dose taken at the same time each day." },
      { n: "02", title: "PDE-5 inhibition", body: "Relaxes smooth muscle and improves blood flow." },
      { n: "03", title: "Long half-life", body: "Single dose lasts 24–36 hours." },
      { n: "04", title: "Clinician review", body: "Quarterly check-ins to monitor dose and tolerability." },
    ],
    stats: [
      { v: "36h", k: "duration of action" },
      { v: "Daily", k: "low-dose option" },
      { v: "1/day", k: "simple routine" },
    ],
    faq: [
      { q: "Daily or as-needed?", a: "Many patients prefer daily for spontaneity. Your clinician will help you choose." },
      { q: "Side effects?", a: "Most common: mild headache, flushing, nasal congestion." },
      { q: "Can I drink on it?", a: "Light alcohol is fine. Heavy drinking reduces effectiveness." },
      { q: "Discreet shipping?", a: "Yes — plain packaging, no mention of medication on the outside." },
    ],
  },
  sildenafil: {
    slug: "sildenafil", name: "SILDENAFIL", category: "Performance", type: "On-Demand Tablet",
    accent: "blue", capColor: "#143788", bodyColor: "#8FB6E8", bottleType: "bottle", label: "SILD",
    tagline: "The original. Fast acting. Proven.",
    bullets: ["Fast Onset", "On-Demand", "Trusted"],
    forSide: "men",
    what: "Sildenafil — the active ingredient in Viagra — is the most-studied PDE-5 inhibitor in history. Fast onset, predictable effect, decades of safety data.",
    benefits: [
      { title: "Fast onset", body: "Works in as little as 30 minutes." },
      { title: "4–5 hour window", body: "Take it when you need it." },
      { title: "Most-studied option", body: "Three decades of clinical data and real-world use." },
      { title: "Affordable", body: "Generic compounded — a fraction of brand-name pricing." },
    ],
    how: [
      { n: "01", title: "Take when needed", body: "One tablet 30–60 minutes before activity." },
      { n: "02", title: "PDE-5 inhibition", body: "Same mechanism as tadalafil, shorter window." },
      { n: "03", title: "Discreet onset", body: "Works subtly — no jolt, just function restored." },
      { n: "04", title: "Adjust as needed", body: "Your clinician will tune your dose over time." },
    ],
    stats: [
      { v: "30min", k: "fast onset" },
      { v: "4–5h", k: "active window" },
      { v: "30yrs", k: "clinical use" },
    ],
    faq: [
      { q: "Sildenafil vs. tadalafil?", a: "Sildenafil acts faster, shorter window. Tadalafil lasts longer. Personal preference." },
      { q: "With food?", a: "Heavy/fatty meals slow onset. Take on lighter stomach for fastest effect." },
      { q: "Will I need it forever?", a: "Many men use it as-needed for years. Some address underlying causes (T, vascular)." },
      { q: "Mix with alcohol?", a: "Light alcohol is fine. Heavy drinking reduces effect significantly." },
    ],
  },
  "pt-141": {
    slug: "pt-141", name: "PT-141", category: "Libido", type: "Subcutaneous Injection",
    accent: "purple", capColor: "#6E2BAE", bodyColor: "#B198D6", bottleType: "vial", label: "PT-141",
    tagline: "The desire molecule. For her, for him.",
    bullets: ["Boosts Libido", "Works Through The Brain", "Both Sexes"],
    forSide: "both",
    what: "PT-141 (bremelanotide) is a melanocortin agonist that acts on the brain to increase sexual desire — not blood flow. It's the only protocol of its kind, and it works for women too.",
    benefits: [
      { title: "Increases desire", body: "Targets the central nervous system, not the vasculature." },
      { title: "For women too", body: "FDA-approved for HSDD in women — rare in this space." },
      { title: "Fast onset", body: "Effects begin within 30–45 minutes." },
      { title: "Use as needed", body: "Not a daily protocol — take when you want it." },
    ],
    how: [
      { n: "01", title: "Subcutaneous injection", body: "Tiny insulin-needle dose at home." },
      { n: "02", title: "Brain-level action", body: "Acts on melanocortin receptors that govern desire." },
      { n: "03", title: "Onset", body: "Effects within 30–45 minutes, lasts up to 24 hours." },
      { n: "04", title: "Tune over time", body: "Most patients dial in dose with their clinician over 2–3 cycles." },
    ],
    stats: [
      { v: "30–45m", k: "onset" },
      { v: "Both", k: "sexes prescribed" },
      { v: "Brain", k: "level mechanism" },
    ],
    faq: [
      { q: "Difference from Viagra?", a: "Viagra is vascular. PT-141 is neurological. Some patients use both — they're complementary." },
      { q: "Side effects?", a: "Most common: mild nausea, facial flushing — short-lived." },
      { q: "Can women use it?", a: "Yes — it's FDA-approved for HSDD (hypoactive sexual desire disorder) in women." },
      { q: "Daily or as-needed?", a: "Take as-needed, not daily. Most patients use it 1–3 times per week." },
    ],
  },
  dhea: {
    slug: "dhea", name: "DHEA", category: "Vitality", type: "Daily Capsule",
    accent: "amber", capColor: "#C76A6A", bodyColor: "#E2B6B6", bottleType: "bottle", label: "DHEA",
    tagline: "The master hormone. Pre-cursor to everything.",
    bullets: ["Hormonal Vitality", "Energy + Mood", "Anti-Aging"],
    forSide: "both",
    what: "DHEA is the precursor your body uses to build testosterone, estrogen, and other downstream hormones. Levels decline ~10% per decade after 30 — supplementation restores the upstream supply.",
    benefits: [
      { title: "Hormonal precursor", body: "Body converts it to T and estrogen as needed." },
      { title: "Energy & mood", body: "Many patients report cleaner energy and steadier mood." },
      { title: "Bone & muscle support", body: "Mild benefits to lean mass and bone density." },
      { title: "Skin & vitality", body: "Improvements in skin hydration and visible vitality." },
    ],
    how: [
      { n: "01", title: "Daily capsule", body: "Once daily, taken with breakfast." },
      { n: "02", title: "Liver conversion", body: "Body converts DHEA to active hormones as needed." },
      { n: "03", title: "Restores upstream", body: "Replaces what age-related decline has reduced." },
      { n: "04", title: "Monitored", body: "Quarterly labs to track DHEA-S, T, and estradiol." },
    ],
    stats: [
      { v: "Daily", k: "easy capsule" },
      { v: "10%", k: "per-decade decline" },
      { v: "T/E", k: "precursor to both" },
    ],
    faq: [
      { q: "Is DHEA safe?", a: "Yes — prescribed at clinician-monitored doses. Self-dosing OTC can push hormones out of balance." },
      { q: "Will it shut down my T?", a: "No — DHEA is upstream, not exogenous T. Doesn't suppress endogenous production." },
      { q: "Women too?", a: "Yes — many women benefit, especially around peri- and post-menopause." },
      { q: "How long until results?", a: "Energy and mood shift within 2–4 weeks. Hormonal labs after 6–8 weeks." },
    ],
  },
  anastrozole: {
    slug: "anastrozole", name: "ANASTROZOLE", category: "Hormones", type: "Tablet",
    accent: "purple", capColor: "#6E2BAE", bodyColor: "#C8B5DD", bottleType: "bottle", label: "ANAS",
    tagline: "Estrogen control for men on TRT.",
    bullets: ["Aromatase Inhibitor", "TRT Adjunct", "Estrogen Balance"],
    forSide: "men",
    what: "Anastrozole is an aromatase inhibitor — it blocks the conversion of testosterone to estrogen. Used as an adjunct in TRT when estradiol climbs too high. Always lab-driven, never blind-dosed.",
    benefits: [
      { title: "Controls estrogen", body: "Prevents T-to-E conversion when E levels rise too high." },
      { title: "TRT optimization", body: "Used when bloodwork shows elevated estradiol." },
      { title: "Reduces water retention", body: "High E can cause bloating — controlling it cleans up the look." },
      { title: "Improves libido & mood", body: "When E is too high or too low, both suffer. Balance matters." },
    ],
    how: [
      { n: "01", title: "Bloodwork first", body: "We don't prescribe blind — your estradiol must justify it." },
      { n: "02", title: "Low, intermittent dose", body: "Crash-low estrogen is worse than high. Less is more." },
      { n: "03", title: "Reassess", body: "Re-check labs after 4–6 weeks; adjust." },
      { n: "04", title: "Quarterly monitoring", body: "Ongoing labs to keep E in the sweet spot." },
    ],
    stats: [
      { v: "Labs", k: "required first" },
      { v: "Low dose", k: "intermittent" },
      { v: "TRT", k: "adjunct" },
    ],
    faq: [
      { q: "Do I need anastrozole on TRT?", a: "Many men don't. Only when bloodwork shows elevated estradiol." },
      { q: "Risk of crashing estrogen?", a: "Real risk if over-dosed. We dose conservatively and re-test." },
      { q: "Alternatives?", a: "Some respond well to lower TRT doses or splitting frequency before needing anastrozole." },
      { q: "How fast does it work?", a: "Estradiol levels respond within 1–2 weeks. Symptomatic improvement may take 3–4." },
    ],
  },
  "omg-caps": {
    slug: "omg-caps", name: "OMG CAPS", category: "Hair Growth", type: "Daily Capsule",
    accent: "amber", capColor: "#C76A6A", bodyColor: "#E2B6B6", bottleType: "bottle", label: "OMG",
    tagline: "Oral hair regrowth. No needle. No mess.",
    bullets: ["Oral Minoxidil", "Daily Capsule", "Regrowth In Months"],
    forSide: "both",
    what: "OMG Caps are oral minoxidil — the most-prescribed hair regrowth medication of the last 5 years. A small daily capsule replaces the messy topical with measurably better adherence and outcomes.",
    benefits: [
      { title: "Regrowth in 3–6 months", body: "Most patients see visible regrowth by month 6." },
      { title: "No topical mess", body: "No greasy scalp, no dripping, no morning ritual." },
      { title: "Works on the crown", body: "Particularly effective for crown thinning where topical struggles." },
      { title: "Compounded options", body: "Stack with finasteride, biotin, or other agents." },
    ],
    how: [
      { n: "01", title: "Daily capsule", body: "Small dose, taken once a day." },
      { n: "02", title: "Vasodilation", body: "Improves blood flow to hair follicles." },
      { n: "03", title: "Hair cycle reset", body: "Pushes follicles back into active growth phase." },
      { n: "04", title: "Lab + photo tracking", body: "Quarterly photos and labs to measure progress." },
    ],
    stats: [
      { v: "92%", k: "see regrowth by 6 mo" },
      { v: "Daily", k: "easy capsule" },
      { v: "12mo", k: "ongoing care" },
    ],
    faq: [
      { q: "Side effects?", a: "Most common: mild fluid retention, increased body hair. Discussed at intake." },
      { q: "Better than topical?", a: "Studies show oral has equal or better outcomes with much higher adherence." },
      { q: "Combine with finasteride?", a: "Common stack — your clinician will design the protocol." },
      { q: "When can I stop?", a: "Hair regrowth is dose-dependent. Most patients stay on long-term." },
    ],
  },
  brogaine: {
    slug: "brogaine", name: "BROGAINE", category: "Hair Restoration", type: "Topical",
    accent: "electricBlue", capColor: "#1E7AE6", bodyColor: "#7FA9D9", bottleType: "bottle", label: "BRO",
    tagline: "Topical regrowth. Compounded for results.",
    bullets: ["Compounded Topical", "Targeted Application", "Stack-Ready"],
    forSide: "men",
    what: "Brogaine is a compounded topical hair-restoration solution — minoxidil + finasteride + adjuncts in a single bottle. Applied directly where hair loss is happening. Stack-ready with oral protocols.",
    benefits: [
      { title: "Targeted application", body: "Treat exactly where hair is thinning — hairline, crown, temples." },
      { title: "Combo formula", body: "Minoxidil + finasteride + adjuncts in one bottle." },
      { title: "Stack with oral", body: "Pair with OMG Caps for compounded effect." },
      { title: "No systemic burden", body: "Topical finasteride dramatically lowers systemic exposure vs. oral." },
    ],
    how: [
      { n: "01", title: "Daily application", body: "Apply twice daily to thinning areas." },
      { n: "02", title: "Local absorption", body: "Active ingredients reach follicles directly." },
      { n: "03", title: "Hair cycle support", body: "Vasodilation + DHT blockade locally." },
      { n: "04", title: "Photo + lab tracking", body: "Track progress quarterly with your care team." },
    ],
    stats: [
      { v: "Topical", k: "lower systemic" },
      { v: "2x/day", k: "easy routine" },
      { v: "Combo", k: "minox + fin" },
    ],
    faq: [
      { q: "Topical finasteride vs. oral?", a: "Topical has much lower systemic exposure — particularly relevant if oral finasteride caused side effects." },
      { q: "Can I use this if I'm on OMG Caps?", a: "Yes — common stack. Your clinician will design the protocol." },
      { q: "How long until results?", a: "Visible regrowth typically by month 4–6." },
      { q: "Side effects?", a: "Mild scalp irritation in some patients. Discussed at intake." },
    ],
  },
};

export const CATEGORIES = [
  {
    key: "weight-loss",
    label: "Weight Loss",
    accent: "pink",
    description: "GLP-1 protocols and metabolic support.",
    meds: ["semaglutide", "tirzepatide", "oral-glp1"],
  },
  {
    key: "performance",
    label: "Performance",
    accent: "blue",
    description: "Testosterone optimization and male performance.",
    meds: ["testosterone", "enclomiphene"],
  },
  {
    key: "wellness",
    label: "Wellness",
    accent: "yellow",
    description: "Energy, metabolism, and recovery support.",
    meds: ["mic-b12", "l-carnitine"],
  },
];

export const ALL_MEDS = Object.values(MEDICATIONS);

// Merge in the extra meds defined in medPricing.js (oral-semaglutide, BPC-157, TB-500, CJC-1295, Vitamin B12).
import { EXTRA_MEDS } from "@/data/medPricing";
Object.assign(MEDICATIONS, EXTRA_MEDS);
